// src/components/layout/Layout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import Toast from 'shared/components/ui/Toast';
import { Avatar } from 'shared/components/ui';
import AccessBlockedScreen from 'domains/auth/pages/AccessBlockedScreen';

// Dias restantes até o vencimento do acesso (a partir de accessExpiresAt)
const daysRemaining = (ts) => {
  const d = ts?.toDate?.() ?? null;
  if (!d) return null;
  return Math.ceil((d - new Date()) / 86400000);
};

// ── Navegação por role ────────────────────────────────────────────────────────
const NAV = {
  admin: [
    { section: 'GESTÃO', items: [
      { to: '/admin/dashboard',        icon: '📊', label: 'Dashboard' },
      { to: '/admin/appointments',     icon: '📅', label: 'Agendamentos', badgeKey: 'pendingConfirmations' },
      { to: '/admin/new-appointment',  icon: '➕', label: 'Novo Agendamento' },
    ]},
    { section: 'FINANCEIRO', items: [
      { to: '/admin/cashflow',         icon: '💰', label: 'Caixa' },
    ]},
    { section: 'CADASTROS', items: [
      { to: '/admin/procedures',       icon: '💆', label: 'Procedimentos', badgeKey: 'pendingProcedures' },
      { to: '/admin/professionals',    icon: '👩‍⚕️', label: 'Profissionais' },
      { to: '/admin/clients',          icon: '👥', label: 'Clientes' },
      { to: '/admin/users',            icon: '🔐', label: 'Usuários' },
      { to: '/admin/schedule-windows', icon: '🗓️', label: 'Janelas de Agenda', badgeKey: 'pendingWindows' },
      { to: '/admin/settings',         icon: '⚙️', label: 'Configurações' },
    ]},
    { section: 'DESENVOLVIMENTO', superAdminOnly: true, items: [
      { to: '/admin/seed', icon: '🧪', label: 'Ambiente de Testes' },
    ]},
  ],
  professional: [
    { items: [
      { to: '/pro/dashboard',      icon: '📊', label: 'Dashboard' },
      { to: '/pro/schedule',       icon: '📅', label: 'Minha Agenda' },
      { to: '/pro/new-appointment',icon: '➕', label: 'Novo Agendamento' },
      { to: '/pro/procedures',     icon: '💆', label: 'Meus Procedimentos' },
      { to: '/pro/profile',        icon: '👤', label: 'Meu Perfil' },
    ]},
  ],
  client: [
    { items: [
      { to: '/client/home',    icon: '🏠', label: 'Início',      badgeKey: 'notifications' },
      { to: '/client/agenda',  icon: '📅', label: 'Minha Agenda' },
      { to: '/client/catalog', icon: '🔍', label: 'Agendar' },
    ]},
  ],
};

// Itens do bottom bar por role (mobile)
const BOTTOM_NAV = {
  admin: [
    { to: '/admin/dashboard',       icon: '📊', label: 'Início' },
    { to: '/admin/appointments',    icon: '📅', label: 'Agenda',   badgeKey: 'pendingConfirmations' },
    { to: '/admin/new-appointment', icon: '➕', label: 'Novo' },
    { to: '/admin/cashflow',        icon: '💰', label: 'Caixa' },
  ],
  professional: [
    { to: '/pro/dashboard',       icon: '📊', label: 'Início' },
    { to: '/pro/schedule',        icon: '📅', label: 'Agenda' },
    { to: '/pro/new-appointment', icon: '➕', label: 'Novo' },
    { to: '/pro/procedures',      icon: '💆', label: 'Serviços' },
    { to: '/pro/profile',         icon: '👤', label: 'Perfil' },
  ],
  client: [
    { to: '/client/home',    icon: '🏠', label: 'Início',  badgeKey: 'notifications' },
    { to: '/client/agenda',  icon: '📅', label: 'Agenda' },
    { to: '/client/catalog', icon: '🔍', label: 'Agendar' },
  ],
};

export default function Layout({ role }) {
  const { userProfile, logout, isSuperAdmin, effectiveAccessStatus } = useAuth();
  const { pendingConfirmations, pendingProcedures, pendingWindows, notifications, toast } = useApp();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(() => window.innerWidth < 768);
  const navigate = useNavigate();

  // Reage ao resize da tela
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fecha o drawer ao sair do mobile
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);

  // ── GUARDA DE ACESSO ────────────────────────────────────────────────────────
  // Após TODOS os hooks (regra dos hooks): bloqueado/suspenso → tela de bloqueio.
  // Super admin nunca é bloqueado (effectiveAccessStatus = 'active').
  if (effectiveAccessStatus === 'blocked' || effectiveAccessStatus === 'suspended') {
    return <AccessBlockedScreen />;
  }

  // Faixa de trial: dias restantes para admin de clínica em período de teste
  const trialDays = effectiveAccessStatus === 'trial'
    ? daysRemaining(userProfile?.accessExpiresAt)
    : null;
  const showTrialBanner = effectiveAccessStatus === 'trial' && trialDays != null;

  const badgeCounts = {
    pendingConfirmations: pendingConfirmations?.length || 0,
    pendingProcedures:    pendingProcedures?.length    || 0,
    pendingWindows:       pendingWindows?.length        || 0,
    notifications:        notifications?.length         || 0,
  };

  // Filtra grupos marcados como superAdminOnly quando o usuário não é super admin
  const allGroups  = NAV[role] || NAV.admin;
  const navGroups  = allGroups.filter(g => !g.superAdminOnly || isSuperAdmin);
  const bottomItems = BOTTOM_NAV[role] || BOTTOM_NAV.admin;
  const roleLabel  = { admin:'Administrador', professional:'Profissional', client:'Cliente' }[role] || '';

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignora erro de logout */ }
    navigate('/login');
  };

  // ── Sidebar / Drawer ────────────────────────────────────────────────────────
  // Funções de render (não componentes) — chamadas inline como {renderSidebar()}.
  // Renderizar como <Sidebar /> criaria um novo tipo de componente a cada render,
  // forçando React a desmontar/remontar toda a subárvore da sidebar.
  const renderSidebar = () => (
    <nav style={{
      width: 260, height: '100vh', background: '#6B5876',
      display: 'flex', flexDirection: 'column',
      position: 'fixed', top: 0, left: 0, zIndex: 200,
      transform: isMobile && !drawerOpen ? 'translateX(-100%)' : 'translateX(0)',
      transition: 'transform 0.25s cubic-bezier(.4,0,.2,1)',
      boxShadow: isMobile && drawerOpen ? '12px 0 40px rgba(0,0,0,0.22)' : 'none',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding:'22px 20px 16px', borderBottom:'1px solid rgba(255,255,255,0.10)',
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:22, color:'#fff' }}>
            Agenda<span style={{ color:'#F5D9DC' }}>Pro</span>
          </div>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.45)', letterSpacing:2,
            textTransform:'uppercase', marginTop:2 }}>{roleLabel}</div>
        </div>
        {isMobile && (
          <button onClick={() => setDrawerOpen(false)}
            style={{ background:'rgba(255,255,255,0.12)', border:'none', cursor:'pointer',
              width:32, height:32, borderRadius:8, color:'#fff', fontSize:20, lineHeight:1,
              display:'flex', alignItems:'center', justifyContent:'center' }}>
            ×
          </button>
        )}
      </div>

      {/* Itens de navegação — flex:1 + minHeight:0 garante scroll correto dentro do flex column */}
      <div style={{ padding:'12px 10px', flex:1, overflowY:'auto', minHeight:0 }}>
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {group.section && (
              <div style={{ fontSize:10, fontWeight:600, letterSpacing:2,
                textTransform:'uppercase', color:'rgba(255,255,255,0.38)',
                padding:'10px 12px 5px' }}>
                {group.section}
              </div>
            )}
            {group.items.map(item => {
              const badge = item.badgeKey ? badgeCounts[item.badgeKey] : 0;
              return (
                <NavLink key={item.to} to={item.to}
                  onClick={() => setDrawerOpen(false)}
                  style={{ textDecoration:'none', display:'block' }}>
                  {({ isActive }) => (
                    <div style={{
                      display:'flex', alignItems:'center', gap:10,
                      padding:'10px 12px', borderRadius:8, cursor:'pointer',
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.70)',
                      fontSize:14, fontWeight: isActive ? 500 : 400,
                      background: isActive ? '#C9929A' : 'transparent',
                      marginBottom:2, transition:'all 0.15s',
                    }}>
                      <span style={{ fontSize:16, width:20, textAlign:'center' }}>{item.icon}</span>
                      <span style={{ flex:1 }}>{item.label}</span>
                      {badge > 0 && (
                        <span style={{ background: isActive ? 'rgba(255,255,255,.22)' : '#C9929A',
                          color:'#fff', fontSize:10, fontWeight:600,
                          padding:'2px 6px', borderRadius:20, minWidth:18, textAlign:'center' }}>
                          {badge}
                        </span>
                      )}
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>

      {/* Perfil + logout */}
      <div style={{ padding:'12px 10px', borderTop:'1px solid rgba(255,255,255,0.10)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:10, borderRadius:8 }}>
          <Avatar name={userProfile?.name || 'User'} color={userProfile?.avatarColor || 'rose'} size="md" />
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:500, color:'#fff',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {userProfile?.name}
            </div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)' }}>{roleLabel}</div>
          </div>
        </div>
        <button onClick={handleLogout}
          style={{ width:'100%', marginTop:4, padding:'8px 16px', background:'transparent',
            border:'none', color:'rgba(255,255,255,0.52)', cursor:'pointer', fontSize:13,
            borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center',
            gap:6, fontFamily:"'DM Sans',sans-serif" }}>
          🚪 Sair
        </button>
      </div>
    </nav>
  );

  // ── Bottom tab bar (mobile) ─────────────────────────────────────────────────
  const renderBottomBar = () => (
    <nav style={{
      position:'fixed', bottom:0, left:0, right:0, zIndex:150,
      background:'#fff', borderTop:'1px solid #E8E5EC',
      display:'flex', alignItems:'stretch',
      boxShadow:'0 -4px 20px rgba(0,0,0,0.08)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {bottomItems.map(item => {
        const badge = item.badgeKey ? badgeCounts[item.badgeKey] : 0;
        return (
          <NavLink key={item.to} to={item.to}
            style={{ textDecoration:'none', flex:1 }}>
            {({ isActive }) => (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                justifyContent:'center', padding:'8px 4px 6px', position:'relative',
                color: isActive ? '#6B5876' : '#A8A1B0' }}>
                {/* Indicador ativo */}
                {isActive && (
                  <div style={{ position:'absolute', top:0, left:'25%', right:'25%',
                    height:2, background:'#6B5876', borderRadius:'0 0 4px 4px' }} />
                )}
                <span style={{ fontSize:20, lineHeight:1, marginBottom:3 }}>{item.icon}</span>
                <span style={{ fontSize:10, fontWeight: isActive ? 600 : 400,
                  fontFamily:"'DM Sans',sans-serif" }}>{item.label}</span>
                {badge > 0 && (
                  <div style={{ position:'absolute', top:6, left:'50%', marginLeft:4,
                    background:'#C9929A', color:'#fff', fontSize:9, fontWeight:700,
                    width:16, height:16, borderRadius:'50%', display:'flex',
                    alignItems:'center', justifyContent:'center' }}>
                    {badge > 9 ? '9+' : badge}
                  </div>
                )}
              </div>
            )}
          </NavLink>
        );
      })}

      {/* Botão "Mais" — abre drawer com menu completo (só admin/pro) */}
      {role !== 'client' && (
        <button onClick={() => setDrawerOpen(true)}
          style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
            justifyContent:'center', padding:'8px 4px 6px', background:'none', border:'none',
            cursor:'pointer', color:'#A8A1B0' }}>
          <span style={{ fontSize:20, lineHeight:1, marginBottom:3 }}>☰</span>
          <span style={{ fontSize:10, fontFamily:"'DM Sans',sans-serif" }}>Menu</span>
        </button>
      )}

      {/* Botão "Sair" para cliente mobile */}
      {role === 'client' && (
        <button onClick={handleLogout}
          style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
            justifyContent:'center', padding:'8px 4px 6px', background:'none', border:'none',
            cursor:'pointer', color:'#A8A1B0' }}>
          <span style={{ fontSize:20, lineHeight:1, marginBottom:3 }}>🚪</span>
          <span style={{ fontSize:10, fontFamily:"'DM Sans',sans-serif" }}>Sair</span>
        </button>
      )}
    </nav>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', minHeight:'100vh' }}>

      {/* Overlay escuro ao abrir drawer */}
      {drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} style={{
          position:'fixed', inset:0,
          background:'rgba(30,20,40,0.45)',
          backdropFilter:'blur(3px)',
          zIndex:199,
        }} />
      )}

      {/* Sidebar — sempre renderiza, posição controlada por transform */}
      {renderSidebar()}

      {/* MAIN */}
      <main style={{
        marginLeft: isMobile ? 0 : 260,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: '#FAF7FB',
        // Espaço para o bottom bar no mobile — inclui safe-area para notch/home indicator
        paddingBottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom, 0px))' : 0,
      }}>
        {/* Topbar */}
        <header style={{
          height: 56, background: '#fff', borderBottom: '1px solid #E8E5EC',
          display: 'flex', alignItems: 'center',
          padding: isMobile ? '0 16px' : '0 28px',
          gap: 12, position: 'sticky', top: 0, zIndex: 100,
        }}>
          {/* Hamburguer (mobile, abre drawer — só admin/pro) */}
          {isMobile && role !== 'client' && (
            <button onClick={() => setDrawerOpen(true)}
              style={{ background:'none', border:'none', cursor:'pointer',
                padding:'6px 4px', display:'flex', flexDirection:'column',
                gap:4.5, flexShrink:0 }}>
              <span style={{ width:22, height:2, background:'#3D3447', borderRadius:2, display:'block' }} />
              <span style={{ width:22, height:2, background:'#3D3447', borderRadius:2, display:'block' }} />
              <span style={{ width:16, height:2, background:'#3D3447', borderRadius:2, display:'block' }} />
            </button>
          )}

          {/* Logo mobile */}
          {isMobile && (
            <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:20, color:'#6B5876', flex:1 }}>
              Agenda<span style={{ color:'#B5A2D2' }}>Pro</span>
            </div>
          )}

          {!isMobile && <div style={{ flex:1 }} />}

          {/* Notificações */}
          {notifications?.length > 0 && (
            <div style={{ position:'relative', flexShrink:0 }}>
              <div style={{ width:36, height:36, borderRadius:10, border:'1px solid #E8E5EC',
                background:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                cursor:'pointer', fontSize:16 }}>🔔</div>
              <div style={{ position:'absolute', top:6, right:6, width:8, height:8,
                background:'#C9929A', borderRadius:'50%' }} />
            </div>
          )}

          {/* Avatar + nome (desktop) */}
          {!isMobile && (
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <Avatar name={userProfile?.name || ''} color={userProfile?.avatarColor || 'rose'} size="sm" />
              <div style={{ fontSize:13, color:'#3D3447', fontWeight:500, maxWidth:140,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {userProfile?.name}
              </div>
              <button onClick={handleLogout}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:13,
                  color:'#A8A1B0', fontFamily:"'DM Sans',sans-serif", padding:'4px 8px',
                  borderRadius:6 }}>
                Sair
              </button>
            </div>
          )}
        </header>

        {/* Faixa de trial — abaixo da topbar, só para admin de clínica em teste */}
        {showTrialBanner && (
          <div style={{
            background: trialDays <= 5 ? '#FDECEC' : '#FFF8F0',
            borderBottom: `1px solid ${trialDays <= 5 ? '#F3B5B5' : '#FBBF24'}`,
            color: trialDays <= 5 ? '#9a3412' : '#8B6914',
            padding: isMobile ? '10px 14px' : '10px 28px',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            fontSize: 13, fontFamily: "'DM Sans',sans-serif",
          }}>
            <span style={{ fontSize: 15 }}>⏳</span>
            <span style={{ flex: 1, minWidth: 180 }}>
              {trialDays <= 0
                ? <>Seu período de teste termina <strong>hoje</strong>. Ative seu plano para não perder o acesso.</>
                : <>Você está no período de teste — <strong>{trialDays} {trialDays === 1 ? 'dia restante' : 'dias restantes'}</strong>. Fale com o administrador para ativar seu plano.</>}
            </span>
            <a href={`mailto:admin@agendapro.com?subject=${encodeURIComponent(`Ativação de plano — ${userProfile?.name || ''}`)}`}
              style={{ color: 'inherit', fontWeight: 600, textDecoration: 'underline',
                whiteSpace: 'nowrap', cursor: 'pointer' }}>
              Saiba mais →
            </a>
          </div>
        )}

        {/* Conteúdo da página */}
        <div style={{ padding: isMobile ? '16px 14px' : 28, flex:1 }}>
          <Outlet />
        </div>
      </main>

      {/* Bottom navigation — só mobile */}
      {isMobile && renderBottomBar()}

      {/* Toast — posicionado acima do bottom nav no mobile para não sobrepor navegação */}
      {toast && (
        <div style={{
          position: 'fixed', zIndex: 999,
          bottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)' : 24,
          right: 16, left: isMobile ? 16 : 'auto',
          maxWidth: isMobile ? 'none' : 360,
        }}>
          <Toast message={toast.message} type={toast.type} />
        </div>
      )}
    </div>
  );
}
